/**
 * The organization's own word for itself: `organizations.terminology.institution`.
 *
 * Alumnia serves schools, universities and business schools (Juan, 25/09).
 * Copy that talks about the institution (who sends, hosts or owns the data)
 * says "la institución" unless the admin picked what they are in
 * Ajustes › Terminología; then it says "el colegio", "la universidad" or
 * "la escuela de negocios", with the right article in every language.
 *
 * The stored value is one of the three kinds, or the admin's own word
 * ("instituto", "academy"). An own word has no known gender or article, so
 * it only replaces the neutral noun where no article is needed ("Tu
 * {word}"); everywhere else the copy stays neutral.
 *
 * No zod here: this module reaches client bundles (the settings forms, the
 * onboarding header). The save validation lives in the server action.
 */

export const INSTITUTION_KINDS = ['school', 'university', 'business_school'] as const
export type InstitutionKind = (typeof INSTITUTION_KINDS)[number]

/** The longest own word the Terminología tab accepts. */
export const INSTITUTION_CUSTOM_MAX = 40

export type InstitutionNoun =
  | { kind: InstitutionKind }
  | { kind: 'custom'; name: string }
  | { kind: 'none' }

export function isInstitutionKind(value: unknown): value is InstitutionKind {
  return (INSTITUTION_KINDS as readonly unknown[]).includes(value)
}

/** What the organization calls itself, read from its `terminology` JSON. */
export function readInstitution(terminology?: Record<string, unknown> | null): InstitutionNoun {
  const raw = terminology?.institution
  const value = typeof raw === 'string' ? raw.trim() : ''
  if (!value) return { kind: 'none' }
  if (isInstitutionKind(value)) return { kind: value }
  return { kind: 'custom', name: value }
}

/**
 * The ICU arguments the institution copy takes:
 * `{institution, select, school {…} university {…} business_school {…} custom {… {institutionName} …} other {…}}`.
 * `other` is the neutral copy, used when nothing is set and, in sentences
 * that need an article, for an own word too.
 */
export function institutionArgs(noun: InstitutionNoun): {
  institution: InstitutionKind | 'custom' | 'none'
  institutionName: string
} {
  return {
    institution: noun.kind,
    institutionName: noun.kind === 'custom' ? noun.name : '',
  }
}

/** `institutionArgs(readInstitution(terminology))`. */
export function institutionArgsFrom(terminology?: Record<string, unknown> | null) {
  return institutionArgs(readInstitution(terminology))
}

export type InstitutionArgs = ReturnType<typeof institutionArgs>

/** The arguments of an organization that has not said what it is. */
export const NEUTRAL_INSTITUTION: InstitutionArgs = institutionArgs({ kind: 'none' })

/**
 * The kind, for copy that only has the three nouns (emails: a label has no
 * `custom` form): undefined when unset or an own word.
 */
export function institutionKindFrom(terminology: unknown): InstitutionKind | undefined {
  const noun = readInstitution(
    terminology && typeof terminology === 'object' && !Array.isArray(terminology)
      ? (terminology as Record<string, unknown>)
      : null
  )
  return noun.kind === 'none' || noun.kind === 'custom' ? undefined : noun.kind
}
